// Avantages validés par QR code (D-156) : ce que les écrans demandent à la base. Le membre ouvre sa fiche (la clé du QR,
// une fois), suit la validation, garde l'appui de 5 s ; la page du scan (sans compte) et « Taper un code » valident ; le
// partenaire et le bureau lisent le suivi. Toutes les règles sont dans la base (migration 20261105800000_avantages_qr.sql).
import { supabase } from './supabase';
import type { PerkUse } from './api';
import type { PerkFrequency } from '../domain/pictos';
import type { QrMethod, QrVia } from '../domain/perk-qr';

const db = () => {
  if (!supabase) throw new Error('Base non configurée');
  return supabase;
};
async function must<T = unknown>(q: PromiseLike<{ data: unknown; error: { message: string; code?: string } | null }>): Promise<T> {
  const { data, error } = await q;
  if (error) throw Object.assign(new Error(error.message), { code: error.code });
  return data as T;
}
const rpc = <T>(fn: string, args: Record<string, unknown> = {}) => must<T>(db().rpc(fn, args));

// --- Le membre ------------------------------------------------------------------------------------------------------
export interface PassOpen {
  id: string; ref: string; key: string; period: number; now: number; expires_at: string;
  who: string; for: string | null; title: string; partner: string | null; stock: number | null; stock_left: number | null;
}
export const openPass = (perkId: string, personId: string | null) => rpc<PassOpen>('perk_pass_open', { p_perk: perkId, p_person: personId });
export interface PassState {
  status: 'open' | 'used' | 'replaced' | 'expired' | 'gone'; used_at?: string | null; method?: QrMethod | null; via?: QrVia | null;
  validator?: string | null; post?: string | null; partner?: string | null; title?: string; stock?: number | null; stock_left?: number | null;
}
export const passState = (passId: string) => rpc<PassState>('perk_pass_state', { p_pass: passId });
export const passHold = (passId: string) => rpc<PerkUse>('perk_pass_hold', { p_pass: passId });

export interface KidPerk {
  id: string; title: string; details: string | null; picto: string; frequency: PerkFrequency; partner_name: string | null;
  person_id: string; person_name: string; block: string | null; stock: number | null; stock_left: number | null; exhausted_at: string | null;
}
export const perksForKids = (clubId: string) => rpc<KidPerk[] | null>('perks_for_kids', { p_club: clubId }).then(x => x ?? []);

// --- La page du scan, le code tapé ------------------------------------------------------------------------------------
export type ScanState = 'ready' | 'done' | 'unknown' | 'login' | 'no_service' | 'used' | 'expired' | 'exhausted' | 'blocked' | 'invalid'
  | 'nomatch' | 'ambiguous' | 'too_many';
export interface ScanResult {
  state: ScanState;
  club?: { name: string; short: string; logo: string | null; color: string | null; slug: string | null };
  perk?: { title: string; picto: string; frequency: PerkFrequency; partner: string | null; partner_logo: string | null };
  validator?: { via?: QrVia; name?: string | null; post?: string | null; reason?: string };
  member?: string; team?: string | null; stock?: number | null; stock_used?: number; exhausted_at?: string | null;
  scanned_at?: string; until?: string; used_at?: string; used_how?: QrMethod; by_me?: boolean; block?: string; mail_hint?: string | null;
  code?: string;
}
export const scanPass = (token: string, link: string | null) => rpc<ScanResult>('perk_scan', { p_token: token, p_link: link });
export const validatePass = (token: string, link: string | null) => rpc<ScanResult>('perk_validate', { p_token: token, p_link: link });
export const codeCheck = (code: string, link: string | null) => rpc<ScanResult>('perk_code_check', { p_code: code, p_link: link });
export const codeValidate = (code: string, link: string | null) => rpc<ScanResult>('perk_code_validate', { p_code: code, p_link: link });
export const sendSpaceLink = (token: string) => rpc<{ sent: boolean; hint?: string | null; again?: boolean }>('perk_scan_send_link', { p_token: token });

// --- Le suivi -------------------------------------------------------------------------------------------------------------
export interface PartnerPerkCount { id: string; title: string; active: boolean; stock: number | null; stock_used: number; exhausted_at: string | null; today: number; month: number; total: number }
export interface PartnerUse { perk_id: string; title: string; used_at: string; method: QrMethod; via: QrVia | null; validator: string | null; mine: boolean; code: string }
export interface PartnerValidations { club_slug: string | null; perks: PartnerPerkCount[]; uses: PartnerUse[] }
export const partnerValidations = (partnerId: string | null, link: string | null) =>
  rpc<PartnerValidations | null>('partner_validations', { p_partner: partnerId, p_link: link });

export interface ClubValidation {
  id: string; perk_id: string; title: string; picto: string; partner_name: string | null; used_at: string; method: QrMethod; via: QrVia | null;
  validator: string | null; post: string | null; member: string; code: string;
}
export const perkValidations = (clubId: string) => rpc<ClubValidation[] | null>('perk_validations', { p_club: clubId }).then(x => x ?? []);

// --- Le lien privé du partenaire, gardé sur ce téléphone (D-033) : le scan le reconnaît ensuite ---------------------------
const LINK_KEY = 'arceau.partner-link';
export function rememberPartnerLink(token: string): void { try { localStorage.setItem(LINK_KEY, token); } catch { /* stockage fermé */ } }
export function partnerLink(): string | null { try { return localStorage.getItem(LINK_KEY); } catch { return null; } }
